import { Fragment, FunctionComponent, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Form from "components/forms/form";
import Col from "components/grid/col";
import Grid from "components/grid/grid";
import Row from "components/grid/row";
import TextInput from "components/forms/inputs/text";
import { useForm } from "react-hook-form";
import { LocaleString } from "models/locale";
import FormButtons, { FormButtonInterface } from "components/forms/buttons";
import { FormSubmitFunction, FormValues } from "lib/form";
import { CancelButton, SubmitButton } from "components/forms/buttons.utils";
import { ListEntity } from "models/list-entity";
import TextareaInput from "components/forms/inputs/textarea";
import Checkbox from "components/forms/inputs/checkbox";
import EntitySchemaTable from "./entity-schema-table";
import { SchemaColumn } from "models/generic";
import { createEmpty } from "lib/locale";
import { useLocales } from "stores/hooks/locale";
import { List } from "models/list";
import Line from "components/layout/line";

interface EntityFormProps {
  list: List;
  entity?: ListEntity;
  existingIdentifiers: string[];
  onSubmit: FormSubmitFunction;
  onClose: () => Promise<void>;
}

export interface FormModel {
  name: LocaleString;
  identifier: string;
}

const EntityForm: FunctionComponent<EntityFormProps> = (props) => {
  const locales = useLocales();
  const {
    list,
    entity = {
      uuid: undefined,
      name: createEmpty("Uusi kokonaisuus", locales),
      schema: [] as SchemaColumn[],
      identifier: "",
      instructions: createEmpty("", locales),
      multi: false,
      confirm_empty: false,
      hide_from_shared: false,
    },
    existingIdentifiers,
    onSubmit,
    onClose,
  } = props;

  const [entitySchema, toggleEntitySchema] = useState<SchemaColumn[]>(
    entity?.schema || [],
  );

  const schema = yup
    .object({
      name: yup.object().validateLocaleString(),
      identifier: yup
        .string()
        // TODO: not yetp possible using custom yupValidator
        .notOneOf(existingIdentifiers, "validate_identifier_in_user"),
      instructions: yup.object(),
      multi: yup.bool(),
      confirm_empty: yup.bool(),
      hide_from_shared: yup.bool(),
    })
    .required();

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: entity.name,
      identifier: entity.identifier,
      instructions: entity.instructions,
      multi: entity.multi,
      confirm_empty: entity.confirm_empty,
      hide_from_shared: entity.hide_from_shared,
    },
  });

  const buttons: FormButtonInterface[] = [
    { ...CancelButton, onClick: onClose },
    { ...SubmitButton },
  ];

  const handleSubmit = async (values: FormValues) => {
    onSubmit({ ...values, schema: entitySchema as any });
  };

  const showSchema = methods.getValues()["multi"] === true;
  return (
    <Form methods={methods} onSubmit={handleSubmit}>
      <Grid>
        <Row>
          <Col>
            <TextInput required label="Nimi" name="name" translations />
          </Col>
        </Row>
        <Row>
          <Col>
            <TextInput
              uppercase
              label="Tunniste"
              name="identifier"
              // can't check identifier for locked entities
              disabled={props.entity?.locked}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <TextareaInput
              label="Ohjeteksti"
              name="instructions"
              translations
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Checkbox
              name="hide_from_shared"
              label="Piilota kokonaisuus julkisesta näkymästä."
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Checkbox
              name="multi"
              label="Ilmoitusvelvollisella voi olla monta tälläistä kokonaisuutta"
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Checkbox
              name="confirm_empty"
              label="Ilmoitusvelvollisen vahvistettava tyhjä kokonaisuus"
            />
          </Col>
        </Row>

        {showSchema && entity.uuid && (
          <Fragment>
            <Row>
              <Col>
                <Line />
              </Col>
            </Row>
            <Row>
              <Col>
                <EntitySchemaTable
                  list={list}
                  listEntity={entity}
                  schema={entitySchema}
                  onUpdate={async (schema) => {
                    toggleEntitySchema([...schema]); // create new instance
                  }}
                />
              </Col>
            </Row>
          </Fragment>
        )}
        <FormButtons buttons={buttons} />
      </Grid>
    </Form>
  );
};

export default EntityForm;
